import type { FC, ReactElement } from 'react';
import React, { cloneElement } from 'react';
import cn from 'classnames';
import type { IStyler } from '@alfa-bank/site-util-create-widget/src/models';

import { DEFAULT_THEME } from './config';
import type { TCatalogCardDesktop, TDesktopStyles, TTextWithStyler } from './models';
import { BenefitsContent } from './components/benefits-content';

const HR_MARGIN = '32px';

export const CatalogCardDesktop: FC<TCatalogCardDesktop> = ({
    Aligner,
    Block,
    Button,
    DataLayer,
    EmptyCard,
    Grid,
    Hr,
    Img,
    Layout,
    Link,
    Text,
    advantageFirstHeading,
    advantageFirstText,
    advantageSecondHeading,
    advantageSecondText,
    advantageThirdHeading,
    advantageThirdText,
    backgroundColor,
    backgroundImage,
    buttonAdvCodes,
    buttonClickGa,
    buttonHref,
    buttonText,
    buttonView = 'primary',
    detailView = 'secondary',
    cardClickGA,
    cardLinkHref,
    children,
    detailClickGA,
    detailHref,
    detailText,
    floatImg,
    giftImgAlt,
    giftImgHeight = 10,
    giftImgSrc,
    giftText,
    heading,
    headingClickGA,
    headingLink,
    headingText,
    mainImgAlt,
    mainImgHeight = 100,
    mainImgWidth = 290,
    mainImgSrc,
    onLoadGA,
    preconfigured,
    cardStyler,
    styles,
    theme = DEFAULT_THEME,
    useNamedContainers,
    className,
    ...rest
}) => {
    const newThemeNaming = theme === 'white' ? 'light' : 'dark';

    const marginZero: IStyler = {
        marginTop: 0,
        marginBottom: 0,
    };

    const { mainImg: SlotMainImg } = useNamedContainers(children);

    const card = SlotMainImg ? (
        <>{cloneElement(SlotMainImg as ReactElement)}</>
    ) : (
        mainImgSrc && (
            <Img
                alt={mainImgAlt}
                height={mainImgHeight}
                radiusKind="card"
                responsive
                responsiveKind="new"
                src={mainImgSrc}
                width={mainImgWidth}
            />
        )
    );

    const cardRender = headingLink ? (
        <DataLayer data={cardClickGA}>
            <Link border="hidden" href={headingLink} theme={theme}>
                {floatImg ? (
                    <div
                        className={cn({
                            [styles.floatImg as keyof TDesktopStyles]: floatImg,
                        })}
                        style={{
                            background: `center right / cover no-repeat url(${mainImgSrc})`,
                        }}
                    />
                ) : (
                    card
                )}
            </Link>
        </DataLayer>
    ) : (
        <>{card}</>
    );

    const headerRender = (
        <Text
            styler={{
                marginTop: 0,
                marginBottom: 12,
            }}
            tag="h2"
            theme={newThemeNaming}
            view="medium"
        >
            {headingLink ? (
                <DataLayer data={headingClickGA}>
                    <Link href={headingLink} theme={theme}>
                        {heading}
                    </Link>
                </DataLayer>
            ) : (
                <>{heading}</>
            )}
        </Text>
    );

    const button = (
        <DataLayer data={buttonClickGa}>
            <Link advCodes={buttonAdvCodes} border="hidden" href={buttonHref}>
                <Button block size="s" styler={{ borderRadius: 8 }} view={buttonView}>
                    <Text styler={{ ...marginZero }} theme="dark" view="action-primary-medium">
                        {buttonText}
                    </Text>
                </Button>
            </Link>
        </DataLayer>
    );

    const detailButton = (
        <DataLayer data={detailClickGA}>
            <Button block href={detailHref} size="s" styler={{ borderRadius: 8 }} view={detailView}>
                <Text
                    styler={{ ...marginZero }}
                    theme={theme === 'blue' ? 'dark' : 'light'}
                    view="action-primary-medium"
                >
                    {detailText}
                </Text>
            </Button>
        </DataLayer>
    );

    const advHeadingProps: TTextWithStyler = {
        view: 'xsmall',
        styler: {
            marginTop: 0,
            marginBottom: 4,
        },
        theme: newThemeNaming,
    };

    const advTextProps: TTextWithStyler = {
        view: 'primary-medium',
        styler: {
            ...marginZero,
        },
        theme: newThemeNaming,
    };
    const inner = (
        <EmptyCard
            backgroundColor={backgroundColor}
            backgroundImage={backgroundImage}
            backgroundKind="transparent"
            className={cn(styles.wrapper, styles.external)}
            radiusKind="panel"
            shadowKind="plastic"
            styler={{ paddingTop: 32, borderRadius: 32, ...cardStyler }}
        >
            <DataLayer data={onLoadGA} sendOnRender />
            <Grid
                collapse
                columnsWidth={[32, 290, 32, 754, 32]}
                styler={{
                    marginBottom: 32,
                }}
            >
                <></>
                <>{cardRender}</>
                <></>
                <>
                    {headerRender}
                    <Text styler={marginZero} theme={newThemeNaming}>
                        {headingText}
                    </Text>

                    {advantageFirstHeading && (
                        <Grid
                            collapse
                            columnsWidth={[230, null, 230, null, 230]}
                            styler={{
                                marginTop: 32,
                            }}
                        >
                            <>
                                <Text {...advHeadingProps}>{advantageFirstHeading}</Text>
                                <Text {...advTextProps}>{advantageFirstText}</Text>
                            </>
                            <></>
                            <>
                                <Text {...advHeadingProps}>{advantageSecondHeading}</Text>
                                <Text {...advTextProps}>{advantageSecondText}</Text>
                            </>
                            <></>
                            <>
                                <Text {...advHeadingProps}>{advantageThirdHeading}</Text>
                                <Text {...advTextProps}>{advantageThirdText}</Text>
                            </>
                        </Grid>
                    )}

                    {(giftText || detailText || buttonText) && (
                        <>
                            <Hr marginBottom={HR_MARGIN} marginTop={HR_MARGIN} theme={theme} />
                            <BenefitsContent
                                button={button}
                                buttonText={buttonText}
                                detailButton={detailButton}
                                detailText={detailText}
                                floatImg={floatImg}
                                giftImgAlt={giftImgAlt}
                                giftImgHeight={giftImgHeight}
                                giftImgSrc={giftImgSrc}
                                giftText={giftText}
                                newThemeNaming={newThemeNaming}
                                Text={Text}
                            />
                        </>
                    )}
                </>
                <></>
            </Grid>
        </EmptyCard>
    );

    const content = (
        <Block className={className} notPadded theme={theme}>
            {inner}
        </Block>
    );

    const contentWithLink = cardLinkHref ? <Link href={cardLinkHref}>{content}</Link> : content;

    return preconfigured ? (
        <Block notPadded {...rest} className={className}>
            <Layout>{contentWithLink}</Layout>
        </Block>
    ) : (
        <div {...rest} className={className}>
            {contentWithLink}
        </div>
    );
};
